import React, { useState } from 'react';
import { 
  Lock, 
  Eye, 
  EyeOff, 
  ShieldCheck, 
  AlertCircle, 
  Loader2, 
  Save, 
  KeyRound,
  ShieldAlert,
  Shield
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axiosInstance from '../../../api/axiosInstance';

const PasswordHome = () => {
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const toggleVisibility = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwords.currentPassword || !passwords.newPassword || !passwords.confirmNewPassword) {
      return setError('All fields are required');
    }
    if (passwords.newPassword !== passwords.confirmNewPassword) {
      return setError('New passwords do not match');
    }
    if (passwords.newPassword.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axiosInstance.put('/users/update-password', {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
        confirmPassword: passwords.confirmNewPassword,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Security credentials updated successfully');
      setPasswords({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed. Please check your current password.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <div className="w-full">
      <motion.div 
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="w-full"
      >
        {/* Header Section (Matching Account Settings) */}
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600 shadow-sm">
              <KeyRound className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Security</h2>
              <p className="text-xs sm:text-sm text-gray-500">Manage your authentication preferences</p>
            </div>
          </div>
          <div className="flex w-fit items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100 shadow-sm">
            <Shield size={12} />
            <span className="whitespace-nowrap">Protected</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {/* Information Side (Left) */}
          <div className="lg:col-span-4 space-y-4">
            <div className="p-5 md:p-6 rounded-[1.5rem] bg-slate-50 border border-slate-100 space-y-3">
              <h3 className="text-sm font-bold text-slate-800">Requirements</h3>
              <ul className="space-y-2.5">
                {[
                  { text: 'Min 6 characters', icon: <ShieldCheck size={14} className="text-emerald-500" /> },
                  { text: 'Numbers or symbols', icon: <ShieldCheck size={14} className="text-emerald-500" /> },
                  { text: 'Avoid common words', icon: <ShieldCheck size={14} className="text-emerald-500" /> },
                  { text: 'Case sensitive', icon: <ShieldCheck size={14} className="text-emerald-500" /> }
                ].map((req, idx) => (
                  <motion.li 
                    key={idx}
                    variants={itemVariants}
                    transition={{ delay: 0.2 + (idx * 0.1) }}
                    className="flex items-center gap-2 text-xs text-slate-600 font-medium"
                  >
                    {req.icon}
                    {req.text}
                  </motion.li>
                ))}
              </ul>
            </div>

            <div className="p-5 md:p-6 rounded-[1.5rem] bg-blue-50/50 border border-blue-100/50">
              <div className="flex items-center gap-2 text-blue-700 mb-2">
                <ShieldAlert size={14} />
                <h4 className="font-bold text-[10px] uppercase tracking-wider">Pro Tip</h4>
              </div>
              <p className="text-xs text-blue-600/80 leading-relaxed font-medium"> 
                Use a unique password for Every account.
              </p>
            </div>
          </div>

          {/* Form Side (Right) */}
          <div className="lg:col-span-8">
            <div className="rounded-[1.5rem] border border-slate-100 bg-white shadow-xl shadow-slate-200/40 overflow-hidden">
              <form onSubmit={handleSubmit} className="p-5 sm:p-6 md:p-8">
                
                {/* Status Messages */}
                <AnimatePresence mode="wait">
                  {error && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mb-6 flex items-center gap-3 rounded-xl border border-red-100 bg-red-50 p-4 text-xs font-bold text-red-700"
                    >
                      <AlertCircle size={16} className="shrink-0" />
                      {error}
                    </motion.div>
                  )}
                  {success && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mb-6 flex items-center gap-3 rounded-xl border border-green-100 bg-green-50 p-4 text-xs font-bold text-green-700"
                    >
                      <ShieldCheck size={16} className="shrink-0" />
                      {success}
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="grid grid-cols-1 gap-6">
                  
                  {/* Current Password */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700 flex items-center gap-2">
                      Current Password
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-4 flex items-center text-slate-400 group-focus-within:text-blue-600 transition-colors">
                        <Lock size={16} />
                      </div>
                      <input
                        type={showPassword.current ? 'text' : 'password'}
                        name="currentPassword"
                        placeholder="••••••••"
                        value={passwords.currentPassword}
                        onChange={handleChange}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-10 pr-12 py-2.5 outline-none transition-all duration-300 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 text-slate-800 text-sm font-medium placeholder:text-slate-300"
                      />
                      <button
                        type="button"
                        onClick={() => toggleVisibility('current')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-slate-100 transition-all"
                      >
                        {showPassword.current ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {/* Horizontal Divider */}
                  <div className="h-px bg-slate-100 w-full" />

                  {/* New Passwords Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 flex items-center gap-2">
                        New Password
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-4 flex items-center text-slate-400 group-focus-within:text-blue-600 transition-colors">
                          <Lock size={16} />
                        </div>
                        <input
                          type={showPassword.new ? 'text' : 'password'}
                          name="newPassword"
                          placeholder="••••••••"
                          value={passwords.newPassword}
                          onChange={handleChange}
                          className="w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-10 pr-12 py-2.5 outline-none transition-all duration-300 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 text-slate-800 text-sm font-medium placeholder:text-slate-300"
                        />
                        <button
                          type="button"
                          onClick={() => toggleVisibility('new')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-slate-100 transition-all"
                        >
                          {showPassword.new ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 flex items-center gap-2">
                        Confirm New Password
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-4 flex items-center text-slate-400 group-focus-within:text-blue-600 transition-colors">
                          <Lock size={16} />
                        </div>
                        <input
                          type={showPassword.confirm ? 'text' : 'password'}
                          name="confirmNewPassword"
                          placeholder="••••••••"
                          value={passwords.confirmNewPassword}
                          onChange={handleChange}
                          className="w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-10 pr-12 py-2.5 outline-none transition-all duration-300 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 text-slate-800 text-sm font-medium placeholder:text-slate-300"
                        />
                        <button
                          type="button"
                          onClick={() => toggleVisibility('confirm')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-slate-100 transition-all"
                        >
                          {showPassword.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Action Buttons */}
                <div className="mt-8 pt-6 border-t border-slate-50 flex flex-col sm:flex-row items-center justify-end gap-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={loading}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-8 py-2.5 text-sm font-bold text-white transition-all hover:bg-blue-700 shadow-lg active:scale-95 disabled:opacity-70"
                  >
                    {loading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Save size={16} />
                    )}
                    Update Password
                  </motion.button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PasswordHome;